import { ChangeEvent, useState } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { addAlpha } from '@algoan/deep-dive/theme';
import { messages } from '../../modules/bank-statements/bank-statements-messages';
import { BankStatement } from '@algoan/dashboard/model';
import { isRejectedTransaction, monitoring, selectDate } from '@algoan/dashboard/utils';
import { FormattedMoney } from '../atomic-components';
import { EditIcon } from '../icons';
import { useDispatch } from '@algoan/dashboard/store/store';
import { editTransactionComment } from '@algoan/dashboard/store/thunks';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { NotesRow } from '..';
import { Tooltip } from '@algoan/deep-dive/tooltip';

const accountNameColumnWidth = 173;
const dateColumnWidth = 81;
const labelColumnWidth = 360;
const amountColumnWidth = 109;

enum CellStyle {
  HEADER = 'HEADER',
  LEFT_ALIGNED = 'LEFT_ALIGNED',
  RIGHT_ALIGNED = 'RIGHT_ALIGNED',
}

interface Cell {
  width: string;
  data?: string | number;
  color?: string;
  onClick?: () => void;
}

interface ModalTransactionCellProps {
  cell: Cell;
  index: number;
  key: string;
  isRejected?: boolean;
  isSelected: boolean;
  isNoteCell: boolean;
}

interface ModalTransactionRowProps {
  transaction: BankStatement;
  index: number;
  key: string;
  analysisId: string | undefined;
  customerId: string;
}

interface ModalTransactionSectionProps {
  transactionsList: BankStatement[] | undefined;
  analysisId: string | undefined;
  customerId: string;
}

const ModalTransactionCell: React.FC<ModalTransactionCellProps> = ({ cell, index, isRejected, isNoteCell }) => {
  const style = index % 2 === 0 ? CellStyle.LEFT_ALIGNED : CellStyle.RIGHT_ALIGNED;

  return (
    <S.Cell width={cell.width} cellStyle={style} isRejected={isRejected} isNoteCell={isNoteCell}>
      {index === 4 ? (
        <S.EditButton
          data-tooltip-id="transactions-list-comment"
          data-tooltip-content={cell.data ? String(cell.data) : ''}
          onClick={cell.onClick}
        >
          <S.StyledEditIcon showDot={!!cell.data} />
        </S.EditButton>
      ) : (
        <>
          {index === 0 && <S.AccountRow color={cell.color} />}
          <span>{typeof cell.data === 'number' ? <FormattedMoney number={cell.data} /> : cell.data}</span>
        </>
      )}
    </S.Cell>
  );
};

const ModalTransactionRow: React.FC<ModalTransactionRowProps> = ({ transaction, index, analysisId, customerId }) => {
  const intl = useIntl();
  const [notesExpandedRow, setNotesExpandedRow] = useState(false);

  const transactionsCell = [
    {
      width: `${accountNameColumnWidth - 1}px`,
      data: transaction.accountName,
      color: transaction.accountColor,
    },
    {
      width: `${dateColumnWidth}px`,
      data: intl.formatDate(selectDate(transaction.dates)),
    },
    {
      width: `${labelColumnWidth}px`,
      data: transaction.description,
    },
    {
      width: `${amountColumnWidth}px`,
      data: transaction.amount,
    },
    {
      width: `calc(100% - ${accountNameColumnWidth + dateColumnWidth + labelColumnWidth + amountColumnWidth - 9}px)`,
      data: transaction.comment,
      onClick: () => {
        if (!notesExpandedRow) {
          setNotesExpandedRow(!notesExpandedRow);
        }
      },
    },
  ];

  const [textInputValue, setTextInputValue] = useState(transaction.comment ?? '');

  const textHasChanged = textInputValue !== transaction.comment;

  const dispatch = useDispatch();

  const handleNoteSubmit = (): void => {
    setNotesExpandedRow(false);

    dispatch(
      editTransactionComment(
        {
          accountId: transaction.accountId,
          id: transaction.id,
          analysisId: analysisId,
          customerId: customerId,
          previousComment: transaction.comment ?? '',
        },
        textInputValue,
      ),
    ).then(() => {
      logAmplitudeEvent(AmplitudeEvent.TRANSACTION_ADD_COMMENT);
    });
  };

  const maximumNoteCharacterLength = 1000;

  const handleInputOnChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setTextInputValue(event.target.value);

    if (textInputValue?.length === maximumNoteCharacterLength) {
      monitoring.logError({
        fingerprint: 'maximum_note_length_reached',
        message: `The maximum note length of ${maximumNoteCharacterLength} characters has been reached`,
        context: { level: 'warning' },
      });
    }
  };

  return (
    <div>
      <S.RowTransaction
        background={index % 2 ? 'primary' : 'secondary'}
        data-testid={isRejectedTransaction(transaction) ? 'rejected-transaction' : undefined}
      >
        {transactionsCell.map((cell, index) => (
          <ModalTransactionCell
            key={`${transaction.id}-${cell.data}`}
            cell={cell}
            index={index}
            isRejected={isRejectedTransaction(transaction)}
            isSelected={notesExpandedRow}
            isNoteCell={index === 4}
          />
        ))}
      </S.RowTransaction>
      {notesExpandedRow && (
        <NotesRow
          handleInputOnChange={handleInputOnChange}
          index={index}
          textInputValue={textInputValue}
          setTextInputValue={setTextInputValue}
          handleSubmit={handleNoteSubmit}
          maximumNoteCharacterLength={maximumNoteCharacterLength}
          savedNoteValue={transaction.comment}
          textHasChanged={textHasChanged}
          isRecategorization={true}
        />
      )}
    </div>
  );
};

const ModalHeader: React.FC = () => {
  const intl = useIntl();

  const headerText = [
    {
      width: `${accountNameColumnWidth}px`,
      data: intl.formatMessage(messages.accountNameHeader),
    },
    {
      width: `${dateColumnWidth}px`,
      data: intl.formatMessage(messages.dateHeader),
    },
    {
      width: `${labelColumnWidth}px`,
      data: intl.formatMessage(messages.labelHeader),
    },
    {
      width: `${amountColumnWidth}px`,
      data: intl.formatMessage(messages.amountHeader),
    },
    {
      width: `calc(100% - ${accountNameColumnWidth + dateColumnWidth + labelColumnWidth + amountColumnWidth - 16}px)`,
      data: '',
    },
  ];

  return (
    <S.HeaderRow>
      {headerText.map((cell, index) => (
        <S.Cell key={`${cell.data}-${index}`} width={cell.width} cellStyle={CellStyle.HEADER}>
          <span>{cell.data}</span>
        </S.Cell>
      ))}
    </S.HeaderRow>
  );
};

export const ModalTransactionSection: React.FC<ModalTransactionSectionProps> = ({
  transactionsList,
  analysisId,
  customerId,
}) => {
  return (
    <S.Container>
      <ModalHeader />
      <S.TransactionsSection>
        {transactionsList && (
          <S.TransactionsList data-testid="cashflow-transactions-list">
            {transactionsList.map((transaction, index) => (
              <ModalTransactionRow
                key={`${transaction.id}`}
                transaction={transaction}
                index={index}
                analysisId={analysisId}
                customerId={customerId}
              />
            ))}
          </S.TransactionsList>
        )}
      </S.TransactionsSection>
      <Tooltip id="transactions-list-comment" />
    </S.Container>
  );
};

const S = {
  Container: styled.div`
    width: 100%;
    max-height: 35vh;
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    font-size: 12px;
    line-height: 15px;
    color: ${(props) => props.theme.algoanBlue};
    overflow: hidden auto;

    *::-webkit-scrollbar {
      width: 10px;
    }
    *::-webkit-scrollbar-track {
      background: white;
    }
    *::-webkit-scrollbar-thumb {
      background-color: lightgrey;
      border-radius: 6px;
    }
  `,
  HeaderRow: styled.div`
    width: calc(100% + 2px);
    height: 40px;
    font-weight: bold;
    text-align: center;
    border-radius: 4px;
    background-color: ${(props) => props.theme.iceBlue};
    display: flex;
    margin-bottom: 16px;
  `,
  Cell: styled.div<{
    width: CSSStyleDeclaration['width'];
    cellStyle: CellStyle;
    isRejected?: boolean;
    isNoteCell?: boolean;
  }>`
    min-height: 40px;
    display: flex;
    align-items: center;
    width: ${(props) => props.width};
    border-right: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} 1px solid;
    font-size: 13px;

    &:last-child {
      border-right: unset;
      padding-right: 7px;
    }

    ${({ isNoteCell, theme }) =>
      isNoteCell
        ? `
        cursor: pointer;
        &:hover {
          background-color: ${addAlpha(theme.algoanBlue, 0.05)};
        }
      `
        : ''}

    span {
      margin-left: 8px;
      margin-right: 8px;
    }
    &:nth-last-child(2) {
      color: ${(props) => (props.isRejected ? props.theme.redNotification : props.theme.algoanBlue)};
    }

    ${({ cellStyle }) => {
      switch (cellStyle) {
        case CellStyle.HEADER:
          return `justify-content: center;
                  &:last-child {
                    padding-right: 0px;
                  }
                  span {
                    font-size: 13px;
                    line-height: 16px;
                    font-weight: bold;
                    white-space: inherit;
                    text-transform: lowercase;
                    &:first-letter {
                      text-transform: uppercase;
                    }
                  }`;
        case CellStyle.LEFT_ALIGNED:
          return `justify-content: flex-start;
                  span {
                    padding-top: 8px;
                    padding-bottom: 8px;
                  }
          `;

        case CellStyle.RIGHT_ALIGNED:
          return `justify-content: flex-end;
                  span {
                    padding-top: 8px;
                    padding-bottom: 8px;
                  }
          `;
        default:
          return ``;
      }
    }}
  `,
  TransactionsSection: styled.div`
    width: 100%;
    max-height: 25vh;
    overflow-y: auto;
    overflow-x: hidden;
    border: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} 1px solid;
    border-radius: 4px;

    background:
      linear-gradient(white 30%, rgba(255, 255, 255, 0)),
      linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,
      linear-gradient(rgba(0, 70, 107, 0.1), rgba(0, 0, 0, 0)),
      linear-gradient(rgba(0, 0, 0, 0), rgba(0, 70, 107, 0.1)) 0 100%;

    background-repeat: no-repeat;
    background-size:
      100% 40px,
      100% 40px,
      100% 10px,
      100% 10px;
    background-attachment: local, local, scroll, scroll;
  `,
  TransactionsList: styled.div`
    width: 100%;
  `,
  RowTransaction: styled.div<{ background: string }>`
    width: 100%;
    min-height: 32px;
    display: flex;
    flex-direction: row;
    background-color: ${(props) => (props.background === 'secondary' ? 'rgba(137, 158, 168, 0.05)' : '')};

    &:first-child > :first-child > :first-child {
      height: 100%;
      border-top-left-radius: 3px;
    }

    &:last-child > :first-child > :first-child {
      border-bottom-left-radius: 3px;
    }
  `,
  AccountRow: styled.div<{ color?: string }>`
    min-width: 5px;
    height: calc(100% - 1px);
    align-self: flex-end;
    background: ${(props) => props.color};
  `,
  EditButton: styled.div`
    align-items: center;
    display: flex;
    height: 100%;
    padding-left: 9px;
    width: 100%;
  `,
  StyledEditIcon: styled(EditIcon)`
    margin-bottom: 6px;
  `,
};
